// OneDrive:IgnoreCodeCoverage

import { createSingleEvent, ISingleEventType, ISingleEvent } from '../SingleEventBase';
import { EventFieldType } from '../IEvent';
import { IExtraData } from './IExtraData';

/**
 * @public
 */
export interface IEngagementSingleSchema {
    /**
     * This is the name of the engagement event
     */
    name: string;
    /**
     * This indicates whether it is an intentional action by the user
     */
    isIntentional?: boolean;
    /**
     * This is the name of the scenario
     */
    scenario?: string;
    /**
     * This is the location from where the event took place
     */
    location?: string;
    /**
     * This is what tells if it were a click, tap or a keyboard event
     */
    usageType?: string;
    /**
     * Page where the event took place
     */
    currentPage?: string;
    /**
     * Previous Page
     */
    previousPage?: string;
    /**
     * ABExperiment object used by experiments
     */
    experiment?: {};
    /**
     * The Id of the relevant SharePoint site
     */
    siteId?: string;
    /**
     * The Id of the relevant SharePoint web
     */
    webId?: string;
    /**
     * The Id of the relevant SharePoint list
     */
    listId?: string;
    /**
     * The Id of the relevant SharePoint group
     */
    groupId?: string;
    /**
     * The Id of the relevant SharePoint document
     */
    docId?: string;
    /**
     * The Template Id of the relevant SharePoint web
     */
    webTemplateId?: string;
    /**
     * The Template Id of the relevant SharePoint list
     */
    listTemplateId?: string;
    /**
     * Site Type name of the relevant SharePoint web
     */
    siteType?: string;
    /**
     * Feature name of the relevant recorded action
     */
    feature?: string;
    /**
     * The name of the UX component which was used for the user to perform the action.
     * e.g. 'CommandBar', 'ContextMenu', 'ListRow', 'Tile', etc.
     */
    component?: string;
    /**
     * The object type against which the event was invoked.
     * e.g. 'File', 'Folder', 'List', etc.
     */
    objectType?: string;
    /**
     * The name of the data source from which the target data was obtained.
     * e.g. 'SharePoint', 'ODC', 'Exchange', 'eDiscovery', etc.
     */
    datasource?: string;
    /**
     * The name of the action the user is taking on target data.
     * e.g. 'CopyLink', 'Share', 'Upload', 'Move', etc.
     */
    action?: string;
    /**
     * This is the random extra data for the engagement event
     */
    extraData?: IExtraData;
}

/**
 * @public
 */
export interface IEngagement extends ISingleEvent<IEngagementSingleSchema> {
    data: IEngagementSingleSchema;
}

/**
 * @public
 */
export type Engagement = IEngagement;

/**
 * @public
 */
export const Engagement: IEngagementType = createSingleEvent<IEngagementSingleSchema>({
    eventName: 'Engagement,',
    shortEventName: 'Engagement'
}, {
        name: {
            isKey: true,
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        isIntentional: {
            isPrefixingDisabled: true,
            type: EventFieldType.Boolean
        },
        scenario: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        location: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        usageType: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        currentPage: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        previousPage: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        experiment: {
            isPrefixingDisabled: true,
            type: EventFieldType.Object
        },
        siteId: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        webId: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        listId: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        groupId: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        docId: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        webTemplateId: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        listTemplateId: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        siteType: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        feature: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        component: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        extraData: EventFieldType.Object,
        objectType: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        datasource: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        action: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        }
    });

/**
 * @public
 */
export interface IEngagementType extends ISingleEventType<IEngagementSingleSchema> {
}
